import React from 'react';
import {
  DollarSign,
  Zap,
  CheckCircle,
  AlertTriangle,
  Clock,
  Database,
} from 'lucide-react';
import type { AiCostMetrics } from '../types';
import BudgetGauge from './BudgetGauge';
import ProviderCostChart from './ProviderCostChart';

export interface AiCostSummaryProps {
  data: AiCostMetrics;
  isLoading?: boolean;
  className?: string;
}

const formatCurrency = (value: number): string => {
  if (value >= 1) {
    return `$${value.toFixed(2)}`;
  }
  if (value >= 0.01) {
    return `$${value.toFixed(3)}`;
  }
  return `$${value.toFixed(4)}`;
};

const formatNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

export const AiCostSummary: React.FC<AiCostSummaryProps> = ({
  data,
  isLoading = false,
  className = '',
}) => {
  const { daily, budget, modelHealth } = data;

  // Calculate derived metrics
  const successRate = daily.totalRequests > 0
    ? (daily.successfulRequests / daily.totalRequests) * 100
    : 100;

  const cacheHitRate = (daily.totalInputTokens + daily.totalCachedTokens) > 0
    ? (daily.totalCachedTokens / (daily.totalInputTokens + daily.totalCachedTokens)) * 100
    : 0;

  const costPerRequest = daily.totalRequests > 0
    ? daily.totalCost / daily.totalRequests
    : 0;

  // Count unhealthy models (open circuit breakers)
  const unhealthyModels = Object.values(modelHealth).filter(
    (h) => h.state === 'open'
  ).length;

  const stats = [
    {
      name: 'Daily Spend',
      value: formatCurrency(daily.totalCost),
      icon: DollarSign,
      color: 'bg-blue-500',
      detail: `${formatCurrency(budget.remaining)} remaining`,
    },
    {
      name: 'Total Requests',
      value: daily.totalRequests.toString(),
      icon: Zap,
      color: 'bg-purple-500',
      detail: `${formatCurrency(costPerRequest)}/request`,
    },
    {
      name: 'Success Rate',
      value: `${successRate.toFixed(1)}%`,
      icon: CheckCircle,
      color: successRate >= 95 ? 'bg-green-500' : successRate >= 80 ? 'bg-yellow-500' : 'bg-red-500',
      detail: `${daily.failedRequests} failed`,
    },
    {
      name: 'Avg Latency',
      value: `${Math.round(daily.avgLatencyMs)}ms`,
      icon: Clock,
      color: daily.avgLatencyMs < 2000 ? 'bg-green-500' : daily.avgLatencyMs < 5000 ? 'bg-yellow-500' : 'bg-red-500',
      detail: 'per request',
    },
    {
      name: 'Tokens Used',
      value: formatNumber(daily.totalInputTokens + daily.totalOutputTokens),
      icon: Database,
      color: 'bg-indigo-500',
      detail: `${cacheHitRate.toFixed(0)}% cached`,
    },
    {
      name: 'Model Health',
      value: unhealthyModels === 0 ? 'All Good' : `${unhealthyModels} Issues`,
      icon: AlertTriangle,
      color: unhealthyModels === 0 ? 'bg-green-500' : 'bg-red-500',
      detail: `${Object.keys(modelHealth).length} models`,
    },
  ];

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Budget Alert Banner */}
      {budget.percentUsed >= 80 && (
        <div
          className={`rounded-lg p-4 ${
            budget.percentUsed >= 90
              ? 'bg-red-50 border border-red-200'
              : 'bg-yellow-50 border border-yellow-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle
              className={`h-5 w-5 ${
                budget.percentUsed >= 90 ? 'text-red-500' : 'text-yellow-500'
              }`}
            />
            <div>
              <p
                className={`font-medium ${
                  budget.percentUsed >= 90 ? 'text-red-800' : 'text-yellow-800'
                }`}
              >
                {budget.percentUsed >= 90 ? 'Budget Critical' : 'Budget Warning'}
              </p>
              <p
                className={`text-sm ${
                  budget.percentUsed >= 90 ? 'text-red-600' : 'text-yellow-600'
                }`}
              >
                You've used {budget.percentUsed.toFixed(0)}% of your daily budget.
                {budget.percentUsed >= 90
                  ? ' Processing may be limited.'
                  : ' Consider reviewing usage.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid and Budget Gauge */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Budget Gauge */}
        <div className="card p-6 flex flex-col items-center justify-center">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Daily Budget Usage</h3>
          <BudgetGauge used={budget.used} total={budget.daily} size="lg" />
        </div>

        {/* Stats Grid */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.name} className="card p-4">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-lg ${stat.color}`}
                >
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">{stat.name}</p>
                  <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Provider Cost Breakdown */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">Cost by Provider</h3>
        </div>
        <div className="p-6">
          <ProviderCostChart data={daily.byProvider} />
        </div>
      </div>

      {/* Task Type Breakdown */}
      {Object.keys(daily.byTaskType).length > 0 && (
        <div className="card">
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-medium text-gray-900">Cost by Task Type</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Object.entries(daily.byTaskType)
                .sort(([, a], [, b]) => b.cost - a.cost)
                .map(([taskType, stats]) => (
                  <div
                    key={taskType}
                    className="flex items-center justify-between rounded-lg bg-gray-50 p-4"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900 capitalize">
                        {taskType.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {stats.requests} requests
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(stats.cost)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatNumber(stats.tokens)} tokens
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AiCostSummary.displayName = 'AiCostSummary';

export default AiCostSummary;
